import ToolTip from "components/design/toolTip";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Link } from "components/DesignSystem/Link/Link";
import Modal from "components/DesignSystem/Modal/Modal";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { QuestionWithCircle } from "components/icons/QuestionWithCircle";
import { ApproveFilingDraft } from "components/MessageBubble/ApproveFilingDraft";
import { ParseDftLoader } from "components/MessageBubble/FilingDraft/ParseDftLoader";
import { RejectFilingDraft } from "components/MessageBubble/RejectFilingDraft";
import { useModal } from "hooks/useModal";
import { useGetPreviewUrlQuery } from "store/apis/previewUrl";
import { PdfPreview } from "components/PreviewModal";
import { Tooltip } from "stream-chat-react";
import { ModalProps } from "types/utils/modal";
import { formatNumber } from "utils/formatNumber";

const SectionDescription = ({
  approvalStatus,
  rejectionReason,
  submittedBy,
}: {
  approvalStatus: boolean | null;
  rejectionReason: string | null;
  submittedBy: string | null;
}) => {
  if (approvalStatus === false) {
    return (
      <div className="t-space-y-1">
        <div className="t-text-body-sm t-text-text-60">
          Reason for rejection
        </div>
        <div className="t-text-body-sm t-text-text-30 t-p-3 t-rounded-md t-bg-neutral-0 t-border t-border-solid t-border-neutral-10">
          {rejectionReason}
        </div>
        <div className="t-text-body-sm t-text-text-30 t-italic">
          Rejected by {submittedBy}
        </div>
      </div>
    );
  }

  if (approvalStatus === true) {
    return (
      <div className="t-text-body-sm t-text-text-30 t-italic">
        Approved by {submittedBy}
      </div>
    );
  }

  return (
    <div className="t-text-body-sm t-text-text-30">
      Please review the draft carefully before proceeding. Approve it if all
      information is accurate, or reject it if any corrections are required.
    </div>
  );
};

const ModalTitle = ({ approvalStatus }: { approvalStatus: boolean | null }) => {
  if (approvalStatus === false) {
    return (
      <div className="t-flex t-items-center t-gap-3">
        Filing Draft
        <Tag tagType="red" rounded>
          Rejected
        </Tag>
      </div>
    );
  }
  if (approvalStatus === true) {
    return (
      <div className="t-flex t-items-center t-gap-3">
        Final Filing Draft
        <Tag tagType="green" rounded>
          Approved
        </Tag>
      </div>
    );
  }
  return <>Send Filing Draft For Review</>;
};

export const ReviewDftDraftFiling = ({
  isOpen,
  close,
  draftId,
  groupId,
  isSubmitted,
  approvalStatus,
  rejectionReason,
  submittedBy,
  fileId,
  taxAmount,
  grossAssetValue,
}: ModalProps & {
  draftId: string;
  groupId: string;
  isSubmitted: boolean;
  approvalStatus: boolean | null;
  rejectionReason: string | null;
  submittedBy: string | null;
  fileId: string;
  taxAmount: number;
  grossAssetValue: number;
}) => {
  const discardDraft = useModal();
  const sendConfirmation = useModal();
  const {
    data: fileData,
    isLoading: fileDataLoading,
    isSuccess: fileDataSuccess,
  } = useGetPreviewUrlQuery(
    {
      groupId: groupId,
      fileId: fileId,
    },
    {
      skip: !fileId || !groupId,
      refetchOnMountOrArgChange: true,
    }
  );

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={close}>
        <Modal.Content size="xxl">
          <Modal.Header>
            <Modal.Title>
              <ModalTitle approvalStatus={approvalStatus} />
            </Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body className="!t-p-0 t-h-full">
            <Async.Root
              {...{
                isLoading: fileDataLoading,
                isEmpty: false,
                isSuccess: fileDataSuccess,
              }}
              customLoader={<ParseDftLoader />}
            >
              <Async.Success>
                <div className="t-flex t-gap-1 sm:t-flex-col-reverse">
                  <div className="t-flex-1 t-h-[500px] sm:t-h-[60vh] sm:t-flex-none">
                    <PdfPreview
                      preview_url={fileData?.preview_url || ""}
                      name="Document Preview"
                    />
                  </div>
                  <div className="t-w-96 t-px-8 t-py-6 sm:t-w-full sm:t-box-border sm:t-px-4">
                    <div className="t-m-0 t-flex t-flex-col t-gap-5">
                      <SectionDescription
                        approvalStatus={approvalStatus}
                        rejectionReason={rejectionReason}
                        submittedBy={submittedBy}
                      />

                      <div className="t-flex t-flex-col t-gap-4">
                        <div>
                          <div className="t-text-body-sm t-text-text-30">
                            Tax payable
                          </div>
                          <div className="t-text-body-sm t-text-text-100">
                            ${formatNumber(taxAmount)}
                          </div>
                          <ToolTip
                            text={
                              <span>
                                Delaware Franchise Tax is calculated using your
                                authorized shares, issued shares, and total
                                gross assets.{" "}
                                <Link
                                  className="!t-text-purple-30"
                                  to="https://www.inkle.ai/free-tools/dft-calculator"
                                >
                                  Learn more
                                </Link>
                              </span>
                            }
                            side="top"
                            collisionPadding={16}
                            propClass="!t-text-left t-w-72"
                          >
                            <span className="t-text-body-sm-lite t-italic t-text-neutral-40 t-w-fit t-flex t-items-center t-gap-0.5">
                              How is it calculated
                              <QuestionWithCircle />
                            </span>
                          </ToolTip>
                        </div>
                        <div>
                          <div className="t-text-body-sm t-text-text-30">
                            Gross asset value
                          </div>
                          <div className="t-text-body-sm t-text-text-100">
                            ${formatNumber(grossAssetValue)}
                          </div>
                          <ToolTip
                            text="The gross asset value is calculated by adding up the company’s year-end investments, cash in bank accounts + in hand, fixed assets, crypto, inventory, real estate, loans to subsidiaries, and accounts receivable."
                            side="top"
                            collisionPadding={16}
                            propClass="!t-text-left t-w-72"
                          >
                            <span className="t-text-body-sm-lite t-italic t-text-neutral-40 t-w-fit t-flex t-items-center t-gap-0.5">
                              How is it calculated
                              <QuestionWithCircle />
                            </span>
                          </ToolTip>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Async.Success>
            </Async.Root>
          </Modal.Body>
          <>
            {isSubmitted ? (
              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button>Close</Button>
                </Modal.RawClose>
              </Modal.FooterButtonGroup>
            ) : (
              <Modal.FooterButtonGroup>
                <Button
                  onClick={discardDraft.open}
                  customType="danger_outlined"
                >
                  Reject draft
                </Button>
                <Button customType="primary" onClick={sendConfirmation.open}>
                  Approve draft
                </Button>
              </Modal.FooterButtonGroup>
            )}
          </>
        </Modal.Content>
      </Modal.Root>
      <RejectFilingDraft
        isOpen={discardDraft.isOpen}
        close={discardDraft.close}
        groupId={groupId}
        draftId={draftId}
        closeParent={close}
      />
      <ApproveFilingDraft
        isOpen={sendConfirmation.isOpen}
        close={sendConfirmation.close}
        groupId={groupId}
        draftId={draftId}
        closeParent={close}
      />
    </>
  );
};
